<template>
  <div class="layout-wrap">
    <header ref="header">
      <div class="toggle-wrap">
        <button class="secondary" @click.stop="toggle" style="display: flex !important; visibility: visible !important; opacity: 1 !important;">
          <i class="material-icons dimmed">{{open?'chevron_left':'menu'}}</i>
        </button>
      </div>
      <div class="header-content-wrap">
        <slot name="header"/>
      </div>
    </header>
    <div ref="main" class="push-box" :class="side">
      <div ref="sidebar" v-click-outside="pushOut" class="push-sidebar" :class="{collapsible, open}">
        <div ref="sidebar-content" class="sidebar-content" :class="{'opacity-0':!open}">
          <slot name="sidebar" :refresh="setPushMargin"/>
        </div>
      </div>
      <div class="layout-content-wrap" :class="{collapsible}">
        <slot name="content"/>
      </div>
    </div>
  </div>
</template>

<script>
import ClickOutside from 'vue-click-outside'
import { mapState } from 'vuex'
export default {
  name: 'LayoutWithPushSidebar',
  directives: { ClickOutside },
  props: {
    responsive: Boolean,
    side: { type: String, default: 'left', validator: v => ['left', 'top', 'right', 'bottom'].includes(v) }
  },

  data: () => ({
    collapsible: true,
    open: false,
    contentMinWidth: 550,
    headerH: 0,
    initialLoad: true
  }),

  created () {
    // Set initial push margin to 0 to avoid empty space on left
    // Ensure sidebar starts closed
    this.open = false
    document.documentElement.style.setProperty('--push-margin-left', '0px')
    document.documentElement.style.setProperty('--push-margin-header-left', '0px')
  },

  computed: {
    ...mapState(['editorToOpen', 'contentLoaded']),
    flow () {
      return this.side === 'left' || this.side === 'right' ? 'row' : 'column'
    }
  },

  watch: {
    collapsible (val) {
      // Don't auto-open sidebar - let user control it via toggle button
      // Only close if it's collapsible and open
      if (val && this.open) {
        this.pushOut()
      }
    },
    editorToOpen () {
      this.popupItem = document.getElementById('main-editor')
    },
    $route () {
      // Close sidebar when route changes (menu item clicked)
      if (this.collapsible && this.open) {
        this.pushOut()
      }
    }
  },

  mounted () {
    // window.addEventListener('resize', this.setTopMarginToHeaderHeight)
    this.setTopMarginToHeaderHeight()
    // Always set up mobile detection, not just when responsive prop is set
    // Ensure sidebar is closed on initial load to prevent content shift
    this.open = false
    this.setPushMargin()
    this.setSidebarCollapsible()
    // Mark initial load as complete after a short delay
    this.$nextTick(() => {
      setTimeout(() => {
        this.initialLoad = false
      }, 100)
    })
    if (this.responsive) {
      window.addEventListener('resize', this.setSidebarCollapsible)
    } else {
      // Also listen for resize on mobile even without responsive prop
      const isMobile = window.innerWidth <= 480
      if (isMobile) {
        window.addEventListener('resize', this.setSidebarCollapsible)
      }
    }
  },

  methods: {
    setSidebarCollapsible () {
      // Safety check: ensure sidebar ref is available
      if (!this.$refs.sidebar) {
        return
      }
      const maxWidth = this.$refs.sidebar.getBoundingClientRect().width + this.contentMinWidth
      this.collapsible = window.innerWidth < maxWidth
      if (this.collapsible && this.open) {
        this.pushOut()
      }
      // Don't auto-open sidebar on initial load - keep it closed to prevent content shift
      // Only auto-open if it's not the initial load and there's enough space
      if (!this.collapsible && !this.open && !this.initialLoad) {
        this.pushIn()
      }
    },

    toggle (e) {
      this.open = !this.open
      this.setPushMargin()
    },

    pushOut () {
      if (this.collapsible) {
        this.open = false
        this.setPushMargin()
      }
    },

    pushIn () {
      this.open = true
      this.setPushMargin()
    },

    setPushMargin () {
      const sidebar = this.$refs.sidebar
      const sidebarContent = this.$refs['sidebar-content']
      if (!sidebar || !sidebarContent) return
      let size
      if (this.side === 'left' || this.side === 'right') {
        size = sidebarContent.getBoundingClientRect().width || 300
        if (sidebar.getBoundingClientRect().width !== size) sidebar.style.width = `${size}px`
      } else {
        size = sidebarContent.getBoundingClientRect().height
        if (sidebar.getBoundingClientRect().height !== size) sidebar.style.height = `${size}px`
      }
      // When sidebar is closed, use negative margin to pull content to the left
      // When sidebar is open, margin is 0 (sidebar takes its space)
      const margin = this.open ? 0 : -size
      const marginHeader = this.open ? size - 48 : 0
      document.documentElement.style.setProperty(`--push-margin-${this.side}`, `${margin}px`)
      document.documentElement.style.setProperty(`--push-margin-header-${this.side}`, `${marginHeader}px`)
    },

    setTopMarginToHeaderHeight (e) {
      // if (!this.$refs.header || !this.$refs.main) return
      // const h = this.$refs.header.getBoundingClientRect().height
      // if (h !== this.headerH) {
      //   this.$refs.main.style.marginTop = `${h}px`
      //   this.headerH = h
      // }
    }
  }
}
</script>

<style lang="scss">
  @import "../../../../styles/vars";
  @import "../../../../styles/mixins";

  $max-width-disable: 800px;
  $transition-time: 0.15s;
  .layout-wrap {
    width: 100%;
    max-width: 100%;
    margin: 0;
    padding: 0;
    box-sizing: border-box;

    @media (max-width: $max-width-phone) {
      width: 100%;
      max-width: 100%;
      overflow-x: hidden;
      box-sizing: border-box;
    }

    & > header {
      display: flex;
      //height: $base-size;
      width: 100%;
      max-width: 100%;
      align-items: flex-end;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: $z-index-header;
      box-sizing: border-box;
      margin: 0;
      padding: 0;

      @media (max-width: $max-width-phone) {
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        margin: 0;
        padding: 0;
      }

      &:after {
        /*content: "";
        height: 0;
        width: calc(100% - #{$base-size / 2});
        border-bottom: 1px solid $color-aba-blue;
        position: absolute;
        bottom: 0;
        left: $base-size / 4;*/
      }

      .toggle-wrap {
        width: $base-size;
        height: $base-size;
        display: flex !important;
        align-items: center;
        justify-content: center;
        position: relative;
        visibility: visible !important;
        opacity: 1 !important;
        z-index: $z-index-header + 2;
        &:after {
          content: "";
          position: absolute;
          width: 100%;
          height: 2.25rem;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: $color-bg-semitransparent;
          z-index: -1;
        }
      }
      button {
        position: relative;
        width: $base-size * 0.75;
        height: $base-size * 0.75;
        display: flex !important;
        align-items: center;
        justify-content: center;
        visibility: visible !important;
        opacity: 1 !important;
        cursor: pointer;
      }

      button:after {
        content: '';
        position: absolute;
        width: 75%;
        height: 75%;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        border-radius: 50%;
        background: $color-bg;
        z-index: -1;
      }
      .header-content-wrap {
        flex-grow: 1;
        position: relative;
        left: calc(var(--push-margin-header-left) - 1px);
        transition: left $transition-time;
        width: calc(100% - #{$base-size});
        min-width: 0;
        max-width: 100%;
        overflow: visible;
        box-sizing: border-box;

        @include wider-then-phone {
          width: calc(100% - #{$base-size});
          max-width: 100%;
          overflow: visible;
        }

        @media (max-width: $max-width-phone) {
          left: 0 !important;
          width: calc(100% - #{$base-size});
          max-width: 100%;
          overflow: visible;
        }
      }
    }
    .push-box {
      height: 100%;
      margin-top: var(--logo-main-height);
      display: flex;
      width: 100%;
      max-width: 100%;
      margin-left: 0;
      margin-right: 0;
      padding-left: 0;
      padding-right: 0;
      box-sizing: border-box;

      @media (max-width: $max-width-phone) {
        width: 100%;
        max-width: 100%;
        overflow-x: hidden;
        box-sizing: border-box;
      }

      &.top, &.bottom {
        flex-flow: column;
      }
      &.left {
        margin-left: var(--push-margin-left);
        transition: margin-left $transition-time;
        /*overflow-x: hidden;*/

        @media (max-width: $max-width-phone) {
          margin-left: 0 !important;
        }
      }
      &.right {
        margin-right: var(--push-margin-right);
        transition: margin-right $transition-time;

        @media (max-width: $max-width-phone) {
          margin-right: 0 !important;
        }
      }
      &.top {
        margin-top: var(--push-margin-top);
        transition: margin-top $transition-time;
      }
      &.bottom {
        margin-bottom: var(--push-margin-bottom);
        transition: margin-bottom $transition-time;
      }

      &.left, &.right {
        .layout-content-wrap, .sidebar {
          height: 100%;
        }
      }

      .layout-content-wrap {
        flex-grow: 1;
        padding: $base-padding;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;

        @media (max-width: $max-width-phone) {
          padding: $small-padding;
          width: 100% !important;
          max-width: 100% !important;
          overflow-x: hidden !important;
          overflow-y: auto !important;
          box-sizing: border-box;
          height: calc(100vh - var(--logo-main-height)) !important;
          min-height: calc(100vh - var(--logo-main-height)) !important;
          -webkit-overflow-scrolling: touch;
          scroll-behavior: smooth;
          touch-action: pan-y;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          position: relative !important;
        }

        &.collapsible {
          @media (max-width: $max-width-phone) {
            width: 100%;
            min-width: 100%;
            max-width: 100%;
            box-sizing: border-box;
          }
        }
      }

      .push-sidebar {
        flex-shrink: 0;
        border-right: 1px solid $color-aba-blue;

        @media (max-width: $max-width-phone) {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          height: 100vh !important;
          z-index: 9998 !important;
          background: white !important;
          transform: translateX(-100%) !important;
          transition: transform $transition-time !important;
          width: 80% !important;
          max-width: 300px !important;
          box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1) !important;

          &.open {
            transform: translateX(0) !important;
          }
        }

        .sidebar-content {
          width: min-content;
          z-index: 100;
          transition: opacity $transition-time;
          position: sticky;
          top: $base-size;

          @media (max-width: $max-width-phone) {
            position: relative !important;
            top: var(--logo-main-height) !important;
            width: 100% !important;
            max-width: 100% !important;
          }
        }
      }
    }
  }
</style>
